<!-- --vh modifier while the Chat tab is active: pins the slider to the
     viewport height so the thread scrolls internally and the input row
     stays a fixed footer (shared rule in job-center-v3.scss). -->
<div class="jc-v3-jps-slider" [class.jc-v3-cps-slider--vh]="activeTab === 'chat'">

  <!-- Sticky header -->
  <app-cp-header
    direction="job"
    [title]="jobTitle"
    [titleBadge]="titleBadge"
    [upgradeBadge]="upgradeBadge"
    [subtitle]="job?.clientName || job?.jobClassification"
    [refId]="jobRefId"
    [meta]="headerMeta"
    [matchScore]="matchScore"
    [hideMatchRing]="hideAiInsights"
    matchLabel="Match"
    [initials]="jobInitials"
    [photoUrl]="jobLogo"
    [actions]="headerActions"
    (matchRingClick)="onMatchRingClick()"
    (closeClick)="onClose()"
    (actionClick)="onHeaderAction($event)">
  </app-cp-header>

  <!-- Posted-by strip — Connect / Chat actions reuse the same
       connection-template variant the candidate slider uses, with
       `module="job"` so all connection actions route through
       `job.jobPostedBy`. -->
  <app-cp-rec-strip
    prefix="Posted by"
    [name]="isOwnJobCenterJob ? 'You' : posterName"
    [nameForInitials]="posterName"
    [role]="posterRole"
    [organisation]="posterOrganisation"
    [photoUrl]="posterPhoto">
    <!-- Connect / personal-chat hidden for the viewer's own job on Job
         Center — you can't connect or 1-to-1 chat with yourself. The
         header "Chat" (group) action is unaffected. -->
    <app-connection-template actions
      *ngIf="!isOwnJobCenterJob"
      [page]="'jc-v3-cps-rec-strip'"
      [connectionStatus]="job?.connectionStatus"
      [counterpartsDetails]="job?.user"
      [values]="job"
      [module]="'job'"
      [hideChatUntilConnected]="true">
    </app-connection-template>
  </app-cp-rec-strip>

  <!-- Tab bar — 4 tabs, mirroring the candidate slider's wireframe layout:
       Job Details / Full JD / AI Insights / Chat. The header "Chat" action
       also lands on the Chat tab. -->
  <nav class="jc-v3-cps-tabs" role="tablist">
    <button type="button" class="jc-v3-cps-tab"
            [class.active]="activeTab === 'profile'"
            (click)="selectTab('profile')">Job Details</button>
    <button type="button" class="jc-v3-cps-tab"
            [class.active]="activeTab === 'resume'"
            (click)="selectTab('resume')">Full JD</button>
    <button type="button" class="jc-v3-cps-tab"
            *ngIf="!hideAiInsights"
            [class.active]="activeTab === 'ai'"
            (click)="selectTab('ai')">
      <ng-icon name="tablerBolt"></ng-icon>
      AI Insights
    </button>
    <button type="button" class="jc-v3-cps-tab"
            [class.active]="activeTab === 'chat'"
            [disabled]="chatDisabled"
            (click)="openChatTab()">
      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
      <span class="jc-v3-cps-tab-chat">
        Chat
        <span class="jc-v3-cps-tab-count"
              *ngIf="isOwnJobCenterJob && job?.nonSeenMessageCount > 0">{{ job.nonSeenMessageCount }}</span>
      </span>
    </button>
  </nav>

  <div class="jc-v3-cps-panes">

    <!-- Tab 1 — Job Details -->
    <section class="jc-v3-cps-pane" *ngIf="activeTab === 'profile'">

      <!-- Offered Rate card — same row layout as the candidate slider's
           Submitted For card. Uses a Unicode `$` glyph instead of
           `<ng-icon>` to dodge SVG-wash artifacts.
           Layout strategy (mirror of the candidate slider, roles swapped):
             - Both present  → left "Offered Rate: range", right "Submitted for: X"
             - Only rate     → left "Offered Rate: range", right hidden
             - Only submitted→ left "Submitted for: X",    right hidden -->
      <div class="jc-v3-cps-submitted" *ngIf="payRateDisplay || submittedForDisplay">
        <div class="jc-v3-cps-submitted-row">
          <div class="jc-v3-cps-submitted-main">
            <span class="jc-v3-cps-submitted-ico" aria-hidden="true">$</span>
            <span class="jc-v3-cps-submitted-label">{{ payRateDisplay ? 'Offered Rate:' : 'Submitted for:' }}</span>
            <strong class="jc-v3-cps-submitted-rate">{{ payRateDisplay || submittedForDisplay }}</strong>
          </div>
          <div class="jc-v3-cps-submitted-aux"
               *ngIf="payRateDisplay && submittedForDisplay">
            Submitted for: {{ submittedForDisplay }}
          </div>
        </div>
      </div>

      <!-- Quick Facts card — wireframe wraps title + info grid + tag row
           inside a single bordered surface. -->
      <div class="jc-v3-cps-card">
        <div class="jc-v3-cps-section-title jc-v3-cps-quickfacts-title">Quick Facts</div>

        <!-- Every Quick Facts item always renders its header; missing values
             show a "-" placeholder instead of hiding the row (user request,
             parity with the candidate slider's Quick Facts). The two rows that
             were both labeled "Engagement Type" (engagementType /
             jobClassification) are merged into one so always-on headers don't
             duplicate; engagementType wins when both exist. -->
        <div class="jc-v3-cps-info-grid">
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Experience</div>
            <!-- Briefcase glyph — parity with the candidate slider's
                 Experience item (candidate-profile-slider.component.html). -->
            <div class="jc-v3-cps-info-value">
              <ng-container *ngIf="experienceRange; else dash">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                {{ experienceRange }}
              </ng-container>
            </div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Work Mode</div>
            <div class="jc-v3-cps-info-value">{{ job?.workMode || '-' }}</div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Engagement Type</div>
            <div class="jc-v3-cps-info-value">{{ job?.engagementType || job?.jobClassification || '-' }}</div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Duration</div>
            <!-- durationType (Months/Years) is a separate field — without it
                 the duration showed only the number. Mirrors the
                 work-order wizard's "{{duration}} {{durationType}}". -->
            <div class="jc-v3-cps-info-value">
              <ng-container *ngIf="job?.duration; else dash">{{ job?.duration }} {{ job?.durationType }}</ng-container>
            </div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Client Type</div>
            <div class="jc-v3-cps-info-value">{{ job?.clientType || '-' }}</div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Job ID</div>
            <div class="jc-v3-cps-info-value"
                 [class.jc-v3-cps-info-value--accent-purple]="jobRefId">{{ jobRefId || '-' }}</div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Created On</div>
            <div class="jc-v3-cps-info-value">{{ job?.updatedOn ? (job?.updatedOn | date: 'MM/dd/y') : '-' }}</div>
          </div>
        </div>
        <ng-template #dash>-</ng-template>

        <!-- Tag row always renders the Security Clearance pill so the
             viewer sees the clearance state at a glance:
               true  → green pill "Security Clearance"
               false → red pill "✕ Security Clearance"
             Work-mode / Remote pills remain conditional on their data. -->
        <div class="jc-v3-cps-tag-row" *ngIf="job">
          <span class="jc-v3-cps-tag" data-variant="gray" *ngIf="job?.workMode">{{ job?.workMode }}</span>
          <span class="jc-v3-cps-tag"
                [attr.data-variant]="job?.securityClearance ? 'green' : 'red'">
            <span *ngIf="!job?.securityClearance" class="jc-v3-cps-tag-ico" aria-hidden="true">✕</span>
            Security Clearance
          </span>
          <span class="jc-v3-cps-tag" data-variant="purple" *ngIf="job?.remoteWork">Remote</span>
        </div>
      </div>

      <!-- Skills card — wireframe groups Primary + Secondary skills
           inside one bordered surface, each subsection keeping its
           own uppercase label. -->
      <div class="jc-v3-cps-card" *ngIf="primarySkills.length || secondarySkills.length">
        <section class="jc-v3-cps-section" *ngIf="primarySkills.length">
          <h4 class="jc-v3-cps-section-title">Primary Skills</h4>
          <div class="jc-v3-cps-chips">
            <span class="jc-v3-cps-chip jc-v3-cps-chip-primary"
                  *ngFor="let s of primarySkills; trackBy: trackByIndex">{{ s }}</span>
          </div>
        </section>
        <section class="jc-v3-cps-section" *ngIf="secondarySkills.length">
          <h4 class="jc-v3-cps-section-title">Secondary Skills</h4>
          <div class="jc-v3-cps-chips">
            <span class="jc-v3-cps-chip jc-v3-cps-chip-secondary"
                  *ngFor="let s of secondarySkills; trackBy: trackByIndex">{{ s }}</span>
          </div>
        </section>
      </div>

      <div class="jc-v3-cps-loading" *ngIf="loadingJob && !fullJob">
        Loading full job…
      </div>
    </section>

    <!-- Tab 2 — Full JD -->
    <section class="jc-v3-cps-pane" *ngIf="activeTab === 'resume'">
      <app-cp-jd-body [jobDescription]="job?.jobDescription" [jobTitle]="jobTitle"></app-cp-jd-body>
    </section>

    <!-- Tab 3 — AI Insights. Same `variant="wireframe"` opt-in as the
         candidate slider so both surfaces render the purple-gradient
         score card + bordered Strengths / Gaps cards with gray
         uppercase titles + 👍/👎 feedback buttons (mock parity per
         gigsumo_job_center_v2(16) AI tab). -->
    <section class="jc-v3-cps-pane" *ngIf="activeTab === 'ai' && !hideAiInsights">
      <app-cp-ai-insights
        [pageFrom]="pageFrom"
        [jobId]="job?.jobId"
        [candidateId]="candidateId"
        [variant]="'wireframe'">
      </app-cp-ai-insights>
    </section>

    <!-- Tab 4 — Chat, embedded in the pane (mirrors candidate slider).
         Gated flow (plan check → connection check) runs in openChatTab()
         before messagemodelflag flips; the same 2-pair ghost-bubble
         skeleton covers the gate window. Closing via the widget's X
         returns to the Job Details tab. -->
    <section class="jc-v3-cps-pane jc-v3-cps-pane-chat" *ngIf="activeTab === 'chat'">
      <div class="jc-v3-cps-chat-loading" *ngIf="!messagemodelflag">
        <app-common-skeleton class="chat-skel-v3" [NameOftheScreen]="'messageConversation'" [countforView]="2"></app-common-skeleton>
      </div>
      <app-message-widget
        *ngIf="messagemodelflag"
        [embedded]="true"
        (closeMessageModel)="closeMessage($event)"
        [messageDataPass]="messageData">
      </app-message-widget>
    </section>
  </div>
</div>
